import {
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GroupService } from '../../services/group.service';
import { ToastService } from '../../services/toast.service';
import { GroupInviteModalComponent } from '../modals/group-invite-modal/group-invite-modal.component';

import { Group } from '../../dtos/group';

@Component({
  selector: 'app-join-group',
  templateUrl: './join-group.component.html',
  styleUrls: ['./join-group.component.scss'],
})
export class JoinGroupComponent implements OnInit, OnDestroy {
  inviteToken: string | null = null;
  private sub?: Subscription;
  group: Group | null = null;
  requesting = false;
  errorMessage = '';

  @ViewChild('modalHost', { read: ViewContainerRef }) modalHost?: ViewContainerRef;
  private modalRef: ComponentRef<GroupInviteModalComponent> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private groupService: GroupService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.sub = this.route.queryParamMap.subscribe((params) => {
      const token = params.get('inviteToken');
      this.inviteToken = token && token.trim() ? token : null;

      if (!this.inviteToken) {
        // no token -> redirect home
        this.router.navigate(['/'], { replaceUrl: true });
        return;
      }

      // fetch group info by token
      this.groupService.getGroupInfoByInviteToken(this.inviteToken).subscribe({
        next: (group) => {
          this.group = group;
          // render the modal once we have group info
          this.renderModal();
        },
        error: (error) => {
          console.error('Failed to fetch group info', error);
          this.errorMessage = 'Could not load group information. Please try again.';
          // still render modal so user can try
          this.renderModal();
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.modalRef?.destroy();
  }

  onModalOpenChange(open: boolean): void {
    if (!open) {
      // Modal closed -> go home and remove token
      this.router.navigate(['/'], { replaceUrl: true });
    }
  }

  onRequestJoin(token: string): void {
    if (!token || this.requesting) {
      return;
    }

    this.requesting = true;
    this.errorMessage = '';

    this.groupService.requestJoinByInviteToken(token).subscribe({
      next: () => {
        // success: redirect home (removes token)
        this.toastService.success(
          'Request Sent',
          `Your join request for "${this.group?.name}" has been submitted`,
        );
        this.router.navigate(['/'], { replaceUrl: true });
      },
      error: (error) => {
        console.error('Join request failed', error.error);
        this.errorMessage =
          typeof error?.error === 'string'
            ? error.error
            : error?.error?.detail || 'Could not submit the join request. Please try again.';
        this.toastService.error('Join Request Failed', this.errorMessage);
        this.requesting = false;
        if (this.modalRef) {
          this.modalRef.setInput('requesting', false);
          this.modalRef.setInput('errorMessage', this.errorMessage);
          this.modalRef.changeDetectorRef.detectChanges();
        }
      },
    });
  }

  private renderModal(): void {
    if (!this.modalHost || !this.inviteToken) {
      return;
    }

    if (this.modalRef) {
      // update inputs
      this.modalRef.setInput('open', true);
      this.modalRef.setInput('inviteToken', this.inviteToken);
      this.modalRef.setInput('group', this.group);
      this.modalRef.setInput('requesting', this.requesting);
      this.modalRef.setInput('errorMessage', this.errorMessage);
      this.modalRef.changeDetectorRef.detectChanges();
      return;
    }

    this.modalHost.clear();
    this.modalRef = this.modalHost.createComponent(GroupInviteModalComponent);
    this.modalRef.setInput('open', true);
    this.modalRef.setInput('inviteToken', this.inviteToken);
    this.modalRef.setInput('requesting', this.requesting);
    this.modalRef.setInput('errorMessage', this.errorMessage);
    this.modalRef.setInput('group', this.group);

    this.modalRef.instance.openChange.subscribe((open: boolean) => this.onModalOpenChange(open));
    this.modalRef.instance.requestJoin1.subscribe((token: string) => this.onRequestJoin(token));
  }
}
